import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import {
  getMarianaTekMemberships,
  getMarianaTekReservations,
  getMarianaTekClassSession,
  getMarianaTekMembershipInstance,
  getMarianaTekMembershipTransaction,
  getMarianaTekCreditTransactions,
  getMarianaTekCreditTransaction,
  getMarianaTekOrders,
} from '../services/api'

// Studio information for FSA receipts
const STUDIO_INFO: { [key: string]: { address: string; phone: string } } = {
  'Chelsea': { address: '623 W. 23rd Street, New York, NY 10011', phone: '(646) 755-1412' },
  'Tribeca': { address: '141 West Street, New York, NY 10013', phone: '(212) 220-0630' },
  'East Village': { address: '136 East 13th Street, New York, NY 10003', phone: '(646) 822-8530' },
  'NoMad': { address: '41 East 21st Street, New York, NY 10010', phone: '(917) 409-0983' },
  'Upper East Side': { address: '1500 Third Avenue, New York, NY 10028', phone: '(917) 791-8310' },
  'Williamsburg': { address: '194 Kent Avenue, Brooklyn, NY 11249', phone: '(929) 363-6060' },
  'West Hollywood': { address: '8660 Melrose Avenue, West Hollywood, CA 90069', phone: '(424) 272-9966' },
  'Sherman Oaks': { address: '14622 Ventura Blvd., Sherman Oaks, CA 91403', phone: '(818) 616-6004' },
  'Irvine': { address: '2700 Alton Parkway, Irvine, CA 92606', phone: '(949) 632-2730' },
  'San Diego': { address: '2121 Columbia Street, San Diego, CA 92101', phone: '(619) 365-4750' },
  'San Francisco': { address: '199 Fremont Street, San Francisco, CA 94105', phone: '(415) 528-1760' },
  'Chicago': { address: '151 W. Erie Street, Chicago, IL 60654', phone: '(312) 624-8890' },
  'Miami Beach': { address: '1500 Bay Road, Miami Beach, FL 33139', phone: '(305) 763-8108' },
  'Coral Gables': { address: '4290 Aurora Street, Coral Gables, FL 33146', phone: '(305) 859-0600' },
  'Nashville': { address: '2828 Bransford Avenue, Nashville, TN 37204', phone: '(615) 866-6650' },
  'Austin': { address: '1011 S. Congress Avenue, Austin, TX 78704', phone: '(512) 222-6135' },
  'Dallas': { address: '2615 Howell Street, Dallas, TX 75204', phone: '(972) 339-4020' },
  'Houston': { address: '819 Town and Country Boulevard, Houston, TX 77024', phone: '(346) 803-7530' },
  'Boston': { address: '10 Newbury Street, Boston, MA 02116', phone: '(857) 233-4440' },
  'Seattle': { address: '2400 Westlake Avenue, Seattle, WA 98121', phone: '(206) 486-5070' },
  'London - Finsbury Square': { address: '9-15 Helmsley Place, London E8 3SB', phone: '+44 20 3965 0100' },
  'London - Central St Giles': { address: '1 St Giles High Street, London WC2H 8AG', phone: '+44 20 3965 0100' },
  'London - Canada Water': { address: 'Surrey Quays Road, London SE16 2XU', phone: '+44 20 3965 0100' },
  'London - Aldgate': { address: '1 Braham Street, London E1 8EE', phone: '+44 20 3965 0100' },
}

const Container = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
`

const Section = styled.div`
  margin-bottom: 2rem;
`

const SectionTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
`

const MembershipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const MembershipCard = styled.div<{ selected?: boolean }>`
  background: ${props => props.selected ? '#e6f3ff' : 'white'};
  border: 2px solid ${props => props.selected ? '#007bff' : theme.color.border.light};
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  &:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px ${theme.color.shadow.medium};
  }
`

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
`

const MembershipContent = styled.div`
  flex: 1;
`

const MembershipName = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 0.5rem;
`

const MembershipDetails = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.grey};
`

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.2rem 0.8rem;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: 600;
  text-transform: capitalize;
  margin-left: 0.5rem;

  ${props => {
    switch (props.status) {
      case 'active':
        return `background: #e6ffe6; color: #008000;`
      case 'frozen':
        return `background: #e6f0ff; color: #0056b3;`
      case 'cancelled':
      case 'canceled':
      case 'deactivated':
        return `background: #ffe6e6; color: #d70036;`
      case 'payment_failure':
      case 'pending_failure':
        return `background: #fff3e6; color: #cc7700;`
      case 'pending_customer_activation':
      case 'pending_start_date':
        return `background: #fff9e6; color: #997700;`
      default:
        return `background: #f0f0f0; color: #666;`
    }
  }}
`

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: ${theme.color.background.lightGrey};
  border-radius: 4px;
`

const SelectAllLabel = styled.label`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  cursor: pointer;
`

const BillingCycleHeader = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  background: ${theme.color.background.lightGrey};
  border-radius: 4px;
`

const MembershipGroupHeader = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem 1rem;
  background: ${theme.color.accent.primary};
  border-radius: 4px;
`

const ClassList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`

const ClassCard = styled.div`
  background: white;
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  padding: 1rem 1.5rem;
`

const ClassInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
`

const ClassDetail = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.base};
`

const ClassLabel = styled.span`
  font-weight: 600;
  color: ${theme.color.text.grey};
  margin-right: 0.5rem;
`

const ReceiptSubtotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1.4rem;
  font-weight: 600;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${theme.color.border.light};
  color: ${theme.color.text.base};
`

const ReceiptGrandTotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1.8rem;
  font-weight: 700;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 3px solid ${theme.color.text.base};
  color: ${theme.color.text.base};
`

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2rem;
  border-radius: 4px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: ${theme.color.accent.primary};
    color: white;

    &:hover {
      background: #b5002d;
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: ${theme.color.text.base};
    border: 1px solid ${theme.color.border.light};

    &:hover {
      background: ${theme.color.background.lightGrey};
    }
  `}
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-size: 1.4rem;
`

const Spinner = styled.div`
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid ${theme.color.border.light};
  border-top-color: ${theme.color.accent.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 0.75rem;
  vertical-align: middle;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  color: ${theme.color.text.grey};
  font-size: 1.4rem;
`

const CollapsibleHeader = styled.div<{ expanded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.85;
  }
`

const ExpandIcon = styled.span<{ expanded?: boolean }>`
  font-size: 1.2rem;
  transition: transform 0.2s ease;
  transform: ${props => props.expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  margin-left: 0.75rem;
  color: white;
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const SuccessMessage = styled.div`
  background: #e6ffe6;
  color: #008000;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-style: italic;
`

const ReceiptPreview = styled.div`
  background: white;
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  padding: 2rem;
  margin-top: 2rem;
`

const ReceiptHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  border-bottom: 2px solid ${theme.color.text.base};
  padding-bottom: 1rem;
`

const ReceiptLogo = styled.img`
  height: 40px;
  margin-bottom: 0.5rem;
  object-fit: contain;
`

const ReceiptSubtitle = styled.div`
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
`

const ReceiptSection = styled.div`
  margin-bottom: 1.5rem;
`

const ReceiptSectionTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const ReceiptText = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.base};
  line-height: 1.6;
`

const ReceiptTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  font-size: 1.2rem;
`

const ReceiptTableHeader = styled.th`
  text-align: left;
  padding: 0.75rem;
  background: ${theme.color.background.lightGrey};
  border-bottom: 2px solid ${theme.color.border.light};
  font-weight: 600;
`

const ReceiptTableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid ${theme.color.border.light};
`


interface FSAReceiptsProps {
  userData: any
}

interface MembershipInfo {
  id: string
  name: string
  status: string
  startDate: string
  classCount: number
  renewalRate: number
  billingCycles: BillingCycle[]
}

interface BillingCycle {
  id: string
  startDatetime: string
  endDatetime: string
}

interface ClassAttendance {
  id: string
  className: string
  classType: string
  date: string
  time: string
  location: string
  duration: number
  instructor: string
  costPerClass: number
  billingCycleId?: string
}

interface CreditPackInfo {
  id: string
  name: string
  totalCredits: number
  remainingCredits: number
  purchaseDate: string
  expirationDate: string
  isExpired: boolean
  purchaseCost: number
}

interface CreditPackWithClasses {
  creditPack: CreditPackInfo
  classes: ClassAttendance[]
}

interface MembershipWithClasses {
  membership: MembershipInfo
  classes: ClassAttendance[]
  classesByBillingCycle: Map<string, ClassAttendance[]>
}

const FSAReceipts: React.FC<FSAReceiptsProps> = ({ userData }) => {
  const [memberships, setMemberships] = useState<MembershipInfo[]>([])
  const [selectedMemberships, setSelectedMemberships] = useState<Set<string>>(new Set())
  const [membershipsWithClasses, setMembershipsWithClasses] = useState<MembershipWithClasses[]>([])
  const [isLoadingMemberships, setIsLoadingMemberships] = useState(false)
  const [isLoadingClasses, setIsLoadingClasses] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState('')
  const [error, setError] = useState('')
  const [showReceipt, setShowReceipt] = useState(false)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [emailSuccess, setEmailSuccess] = useState('')
  const [expandedMemberships, setExpandedMemberships] = useState<Set<string>>(new Set())

  // Credit Pack state
  const [creditPacks, setCreditPacks] = useState<CreditPackInfo[]>([])
  const [selectedCreditPacks, setSelectedCreditPacks] = useState<Set<string>>(new Set())
  const [creditPacksWithClasses, setCreditPacksWithClasses] = useState<CreditPackWithClasses[]>([])
  const [isLoadingCreditPacks, setIsLoadingCreditPacks] = useState(false)
  const [expandedCreditPacks, setExpandedCreditPacks] = useState<Set<string>>(new Set())

  const marianaTekId = userData?.user?.external_id
  const clientName = `${userData?.user?.first_name || ''} ${userData?.user?.last_name || ''}`.trim()
  const clientEmail = userData?.user?.email

  const extractMembershipClassCount = (membershipName: string): number => {
    const match = membershipName.match(/(\d+)\s*class/i)
    if (match) return parseInt(match[1], 10)
    if (membershipName.toLowerCase().includes('unlimited')) return 0
    return 0
  }

  useEffect(() => {
    if (marianaTekId) {
      loadMemberships()
      loadCreditPacks()
    }
  }, [marianaTekId])

  const loadMemberships = async () => {
    setIsLoadingMemberships(true)
    setError('')

    try {
      // Fetch ALL membership instances for this user (no status filter)
      const membershipsResponse = await getMarianaTekMemberships(marianaTekId)

      console.log('FSA - Raw membership instances:', membershipsResponse)

      if (!membershipsResponse?.data || membershipsResponse.data.length === 0) {
        setMemberships([])
        return
      }

      const membershipList: MembershipInfo[] = []

      for (const instance of membershipsResponse.data) {
        const attrs = instance.attributes || {}
        const membershipName = attrs.membership_name || attrs.name || 'Membership'
        const status = attrs.status || 'unknown'
        const startDate = attrs.start_datetime || attrs.created_datetime || ''
        const classCount = extractMembershipClassCount(membershipName)

        // Fetch detailed membership instance to get billing_cycles and renewal_rate
        let billingCycles: BillingCycle[] = []
        let renewalRate = 0
        try {
          const detailed = await getMarianaTekMembershipInstance(instance.id)
          const detailedAttrs = detailed?.data?.attributes || {}
          const cycles = detailedAttrs.billing_cycles || []
          billingCycles = cycles.map((c: any) => ({
            id: c.id,
            startDatetime: c.start_datetime,
            endDatetime: c.end_datetime,
          }))
          // Get renewal rate (membership cost per billing cycle)
          renewalRate = parseFloat(detailedAttrs.renewal_rate || detailedAttrs.rate || '0')
          console.log(`FSA - Membership ${instance.id} renewal_rate: ${renewalRate}`)
        } catch (err) {
          console.warn(`Failed to fetch details for membership ${instance.id}:`, err)
        }

        membershipList.push({
          id: String(instance.id),
          name: membershipName,
          status,
          startDate,
          classCount,
          renewalRate,
          billingCycles,
        })
      }

      // Sort: active first, then by start date descending
      membershipList.sort((a, b) => {
        const statusOrder: Record<string, number> = {
          active: 0, frozen: 1, payment_failure: 2, pending_failure: 3,
          pending_customer_activation: 4, pending_start_date: 5,
        }
        const aOrder = statusOrder[a.status] ?? 10
        const bOrder = statusOrder[b.status] ?? 10
        if (aOrder !== bOrder) return aOrder - bOrder
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      })

      console.log('FSA - Processed memberships:', membershipList)
      setMemberships(membershipList)
    } catch (err) {
      console.error('Failed to load memberships:', err)
      setError(err instanceof Error ? err.message : 'Failed to load memberships')
    } finally {
      setIsLoadingMemberships(false)
    }
  }

  const loadCreditPacks = async () => {
    setIsLoadingCreditPacks(true)
    try {
      // Fetch credit transactions and orders in parallel
      const [creditResponse, ordersResponse] = await Promise.all([
        getMarianaTekCreditTransactions(marianaTekId),
        getMarianaTekOrders(marianaTekId),
      ])

      const allTransactions: any[] = creditResponse?.data || []
      const allOrders: any[] = ordersResponse?.data || []

      // Filter for purchase transactions only (these are the credit pack purchases)
      const purchases = allTransactions.filter((t: any) =>
        t.attributes?.origination_type === 'purchase' && t.attributes?.transaction_amount > 0
      )

      console.log(`FSA - Found ${purchases.length} credit pack purchases out of ${allTransactions.length} total transactions`)
      console.log(`FSA - Found ${allOrders.length} orders to match against`)

      // Build a map of order date_placed -> order total for matching
      // The order's date_placed timestamp is identical to the credit transaction's transaction_datetime
      const orderByTimestamp = new Map<string, number>()
      for (const order of allOrders) {
        const datePlaced = order.attributes?.date_placed
        const total = parseFloat(order.attributes?.total || '0')
        if (datePlaced && total > 0) {
          orderByTimestamp.set(datePlaced, total)
        }
      }

      const packList: CreditPackInfo[] = purchases.map((t: any) => {
        const txDatetime = t.attributes.transaction_datetime || ''
        const purchaseCost = orderByTimestamp.get(txDatetime) || 0

        if (purchaseCost > 0) {
          console.log(`FSA - Matched credit pack ${t.id} to order with total: $${purchaseCost}`)
        } else {
          console.log(`FSA - No order match found for credit pack ${t.id} (timestamp: ${txDatetime})`)
        }

        return {
          id: String(t.id),
          name: t.attributes.credit_name || 'Credit Pack',
          totalCredits: t.attributes.transaction_amount,
          remainingCredits: t.attributes.remaining_credits_cache ?? 0,
          purchaseDate: txDatetime,
          expirationDate: t.attributes.expiration_datetime || '',
          isExpired: t.attributes.is_expired || false,
          purchaseCost,
        }
      })

      // Sort: non-expired first, then by purchase date descending
      packList.sort((a, b) => {
        if (a.isExpired !== b.isExpired) return a.isExpired ? 1 : -1
        return new Date(b.purchaseDate).getTime() - new Date(a.purchaseDate).getTime()
      })

      setCreditPacks(packList)
    } catch (err) {
      console.error('Failed to load credit packs:', err)
    } finally {
      setIsLoadingCreditPacks(false)
    }
  }

  const loadClassesForCreditPack = async (creditPack: CreditPackInfo): Promise<CreditPackWithClasses> => {
    console.log(`FSA - Loading classes for credit pack ${creditPack.id}: "${creditPack.name}"`)

    // Fetch reservations from purchase date to now (or expiration)
    const minDate = creditPack.purchaseDate ? creditPack.purchaseDate.split(/[T ]/)[0] : undefined
    const maxDate = new Date().toISOString().split('T')[0]

    console.log(`FSA - Credit pack: fetching reservations from ${minDate || '(no min)'} to ${maxDate}`)

    const reservationsResponse = await getMarianaTekReservations(marianaTekId, undefined, minDate, maxDate)
    const allReservations: any[] = reservationsResponse?.data || []

    // Filter checked-in reservations that used credit_transactions (not membership_transactions)
    const creditReservations = allReservations.filter((res: any) => {
      const status = res.attributes?.status?.toLowerCase()
      const hasCreditTx = res.relationships?.credit_transactions?.data?.length > 0
      const hasMembershipTx = res.relationships?.membership_transactions?.data?.length > 0
      return (status === 'check in' || status === 'checked in') && hasCreditTx && !hasMembershipTx
    })

    console.log(`FSA - Credit reservations (checked-in with credit_transactions): ${creditReservations.length}`)

    // For each, check if its credit_transaction traces back to this credit pack
    const matchedClasses: ClassAttendance[] = []

    for (const res of creditReservations) {
      const creditTxRefs = res.relationships?.credit_transactions?.data || []

      for (const ref of creditTxRefs) {
        try {
          const txDetail = await getMarianaTekCreditTransaction(String(ref.id))
          const parentId = txDetail?.data?.relationships?.parent_credit_transaction?.data?.id

          if (String(parentId) !== String(creditPack.id)) continue

          // Match! Fetch class session details
          const classSessionId = res.relationships?.class_session?.data?.id
          if (!classSessionId) continue

          const classSession = await getMarianaTekClassSession(classSessionId)
          const classAttrs = classSession?.data?.attributes
          if (!classAttrs) continue

          const startDatetime = new Date(classAttrs.start_datetime)
          const locationName = classAttrs.location_display || classAttrs.location_name || "Barry's"

          matchedClasses.push({
            id: res.id,
            className: classAttrs.name || classAttrs.class_type_display || "Barry's Class",
            classType: classAttrs.class_type_display || classAttrs.name || 'Fitness Class',
            date: startDatetime.toLocaleDateString('en-US', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            }),
            time: startDatetime.toLocaleTimeString('en-US', {
              hour: 'numeric', minute: '2-digit', hour12: true,
            }),
            location: locationName,
            duration: classAttrs.duration || 50,
            instructor: classAttrs.instructor_names?.join(', ') || classAttrs.instructors?.map((i: any) => i.name).join(', ') || 'Instructor',
            costPerClass: 0,
          })
          break // Found a match for this reservation, no need to check other credit_txs
        } catch (err) {
          console.warn(`FSA - Failed to check credit_transaction ${ref.id}:`, err)
        }
      }
    }

    // Sort by date
    matchedClasses.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    // Calculate cost per class: total purchase cost split evenly across classes attended
    if (creditPack.purchaseCost > 0 && matchedClasses.length > 0) {
      const costPerClass = creditPack.purchaseCost / matchedClasses.length
      matchedClasses.forEach(c => c.costPerClass = costPerClass)
    }

    console.log(`FSA - Found ${matchedClasses.length} classes for credit pack ${creditPack.id}`)

    return { creditPack, classes: matchedClasses }
  }

  const loadClassesForMembership = async (membership: MembershipInfo): Promise<MembershipWithClasses> => {
    console.log(`FSA - Loading classes for membership ${membership.id}: "${membership.name}"`)

    // Step 1: Fetch ALL reservations for this user during membership period
    // Extract just YYYY-MM-DD from datetime strings (handles both 'T' and space separators)
    const extractDate = (dt: string) => dt ? dt.split(/[T ]/)[0] : undefined
    let minDate = extractDate(membership.startDate)
    if (!minDate && membership.billingCycles.length > 0) {
      minDate = extractDate(membership.billingCycles[0].startDatetime)
    }
    const maxDate = new Date().toISOString().split('T')[0]

    console.log(`FSA - Fetching reservations from ${minDate || '(no min)'} to ${maxDate}`)

    const reservationsResponse = await getMarianaTekReservations(marianaTekId, undefined, minDate, maxDate)
    const allReservations: any[] = reservationsResponse?.data || []

    console.log(`FSA - Total reservations fetched: ${allReservations.length}`)

    // Step 2: Filter for checked-in reservations
    const checkedIn = allReservations.filter((res: any) => {
      const status = res.attributes?.status?.toLowerCase()
      return status === 'check in' || status === 'checked in'
    })

    console.log(`FSA - Checked-in reservations: ${checkedIn.length}`)

    // Log first few reservations to see their relationship structure
    if (checkedIn.length > 0) {
      console.log('FSA - Sample reservation relationships:', JSON.stringify(checkedIn[0].relationships, null, 2))
    }

    // Step 3: For each checked-in reservation, get its membership_transaction ID,
    // then fetch that transaction to check if it belongs to THIS membership instance
    const matchedClasses: ClassAttendance[] = []

    for (const res of checkedIn) {
      // Get membership_transaction ID from reservation relationships
      const mtRef = res.relationships?.membership_transactions?.data
      if (!mtRef || mtRef.length === 0) {
        console.log(`FSA - Reservation ${res.id}: no membership_transactions`)
        continue
      }

      const mtId = mtRef[0].id
      console.log(`FSA - Reservation ${res.id}: has membership_transaction ${mtId}, fetching details...`)

      // Fetch the membership_transaction to get its membership_instance
      try {
        const mtDetail = await getMarianaTekMembershipTransaction(String(mtId))
        console.log(`FSA - membership_transaction ${mtId} relationships:`, JSON.stringify(mtDetail?.data?.relationships, null, 2))

        const miId = mtDetail?.data?.relationships?.membership_instance?.data?.id
        console.log(`FSA - membership_transaction ${mtId} -> membership_instance: ${miId}, looking for: ${membership.id}`)

        if (String(miId) !== String(membership.id)) continue

        // Match! This class was paid for by THIS membership
        const classSessionId = res.relationships?.class_session?.data?.id
        if (!classSessionId) continue

        const classSession = await getMarianaTekClassSession(classSessionId)
        const classAttrs = classSession?.data?.attributes
        if (!classAttrs) continue

        const startDatetime = new Date(classAttrs.start_datetime)
        const locationName = classAttrs.location_display || classAttrs.location_name || "Barry's"

        // Determine which billing cycle this class falls into
        let billingCycleId: string | undefined
        for (const cycle of membership.billingCycles) {
          const cycleStart = new Date(cycle.startDatetime)
          const cycleEnd = new Date(cycle.endDatetime)
          if (startDatetime >= cycleStart && startDatetime <= cycleEnd) {
            billingCycleId = cycle.id
            break
          }
        }

        matchedClasses.push({
          id: res.id,
          className: classAttrs.name || classAttrs.class_type_display || "Barry's Class",
          classType: classAttrs.class_type_display || classAttrs.name || 'Fitness Class',
          date: startDatetime.toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
          }),
          time: startDatetime.toLocaleTimeString('en-US', {
            hour: 'numeric', minute: '2-digit', hour12: true,
          }),
          location: locationName,
          duration: classAttrs.duration || 50,
          instructor: classAttrs.instructor_names?.join(', ') || classAttrs.instructors?.map((i: any) => i.name).join(', ') || 'Instructor',
          costPerClass: 0,
          billingCycleId,
        })
      } catch (err) {
        console.warn(`FSA - Failed to check membership_transaction ${mtId}:`, err)
      }
    }

    // Sort by date
    matchedClasses.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    // Group by billing cycle
    const classesByBillingCycle = new Map<string, ClassAttendance[]>()
    for (const cls of matchedClasses) {
      const key = cls.billingCycleId || 'all'
      if (!classesByBillingCycle.has(key)) {
        classesByBillingCycle.set(key, [])
      }
      classesByBillingCycle.get(key)!.push(cls)
    }

    // Calculate cost per class from renewal rate
    if (membership.renewalRate > 0 && matchedClasses.length > 0) {
      const costPerClass = membership.renewalRate / matchedClasses.length
      matchedClasses.forEach(c => c.costPerClass = costPerClass)
    }

    console.log(`FSA - Found ${matchedClasses.length} classes for membership ${membership.id}`)

    return {
      membership,
      classes: matchedClasses,
      classesByBillingCycle,
    }
  }

  const toggleExpanded = (membershipId: string) => {
    setExpandedMemberships(prev => {
      const newSet = new Set(prev)
      if (newSet.has(membershipId)) {
        newSet.delete(membershipId)
      } else {
        newSet.add(membershipId)
      }
      return newSet
    })
  }

  const handleMembershipToggle = (membership: MembershipInfo) => {
    setSelectedMemberships(prev => {
      const newSet = new Set(prev)
      if (newSet.has(membership.id)) {
        newSet.delete(membership.id)
      } else {
        newSet.add(membership.id)
      }
      return newSet
    })
    setShowReceipt(false)
    setMembershipsWithClasses([])
  }

  const handleSelectAll = () => {
    if (selectedMemberships.size === memberships.length) {
      setSelectedMemberships(new Set())
    } else {
      setSelectedMemberships(new Set(memberships.map(m => m.id)))
    }
    setShowReceipt(false)
    setMembershipsWithClasses([])
  }

  const handleCreditPackToggle = (creditPack: CreditPackInfo) => {
    setSelectedCreditPacks(prev => {
      const newSet = new Set(prev)
      if (newSet.has(creditPack.id)) {
        newSet.delete(creditPack.id)
      } else {
        newSet.add(creditPack.id)
      }
      return newSet
    })
    setShowReceipt(false)
    setCreditPacksWithClasses([])
  }

  const handleSelectAllCreditPacks = () => {
    if (selectedCreditPacks.size === creditPacks.length) {
      setSelectedCreditPacks(new Set())
    } else {
      setSelectedCreditPacks(new Set(creditPacks.map(p => p.id)))
    }
    setShowReceipt(false)
    setCreditPacksWithClasses([])
  }

  const toggleCreditPackExpanded = (packId: string) => {
    setExpandedCreditPacks(prev => {
      const newSet = new Set(prev)
      if (newSet.has(packId)) {
        newSet.delete(packId)
      } else {
        newSet.add(packId)
      }
      return newSet
    })
  }

  const handleLoadClasses = async () => {
    const hasMemberships = selectedMemberships.size > 0
    const hasCreditPacks = selectedCreditPacks.size > 0
    if (!hasMemberships && !hasCreditPacks) return

    setIsLoadingClasses(true)
    setLoadingProgress('')
    setError('')
    setMembershipsWithClasses([])
    setCreditPacksWithClasses([])

    try {
      // Load membership classes
      if (hasMemberships) {
        const membershipResults: MembershipWithClasses[] = []
        const selectedList = memberships.filter(m => selectedMemberships.has(m.id))

        for (let i = 0; i < selectedList.length; i++) {
          const membership = selectedList[i]
          setLoadingProgress(`Loading classes for membership ${i + 1} of ${selectedList.length}: ${membership.name}...`)
          const result = await loadClassesForMembership(membership)
          membershipResults.push(result)
        }

        membershipResults.sort((a, b) =>
          new Date(a.membership.startDate).getTime() - new Date(b.membership.startDate).getTime()
        )
        setMembershipsWithClasses(membershipResults)
      }

      // Load credit pack classes
      if (hasCreditPacks) {
        const creditResults: CreditPackWithClasses[] = []
        const selectedPackList = creditPacks.filter(p => selectedCreditPacks.has(p.id))

        for (let i = 0; i < selectedPackList.length; i++) {
          const pack = selectedPackList[i]
          setLoadingProgress(`Loading classes for credit pack ${i + 1} of ${selectedPackList.length}: ${pack.name}...`)
          const result = await loadClassesForCreditPack(pack)
          creditResults.push(result)
        }

        creditResults.sort((a, b) =>
          new Date(a.creditPack.purchaseDate).getTime() - new Date(b.creditPack.purchaseDate).getTime()
        )
        setCreditPacksWithClasses(creditResults)
      }
    } catch (err) {
      console.error('Failed to load classes:', err)
      setError(err instanceof Error ? err.message : 'Failed to load classes')
    } finally {
      setIsLoadingClasses(false)
      setLoadingProgress('')
    }
  }

  const handleGenerateReceipt = () => {
    setShowReceipt(true)
  }

  const handlePrintReceipt = () => {
    window.print()
  }

  const handleEmailReceipt = async () => {
    if (!clientEmail) {
      setError('No email address found for this client')
      return
    }

    if (membershipsWithClasses.length === 0 && creditPacksWithClasses.length === 0) {
      setError('No receipt to send')
      return
    }

    setIsSendingEmail(true)
    setError('')
    setEmailSuccess('')

    try {
      const receiptElement = document.getElementById('fsa-receipt')
      if (!receiptElement) {
        throw new Error('Receipt element not found')
      }

      const receiptText = receiptElement.innerText
      const subjectSuffix = membershipsWithClasses.length > 1
        ? `${membershipsWithClasses.length} Memberships`
        : membershipsWithClasses[0]?.membership.name || `${creditPacksWithClasses.length} Credit Pack${creditPacksWithClasses.length === 1 ? '' : 's'}`

      const response = await fetch('/api/email/send-fsa-receipt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          marianaTekId,
          operationId: crypto.randomUUID(),
          receiptText,
          subjectSuffix,
        }),
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to send email')
      }

      // The server resolves the recipient from the Mariana Tek profile, which
      // can differ from the SessionM email shown in this view.
      setEmailSuccess('Receipt sent to the customer\'s email on file in Mariana Tek')
    } catch (err) {
      console.error('Failed to send email:', err)
      setError(err instanceof Error ? err.message : 'Failed to send email')
    } finally {
      setIsSendingEmail(false)
    }
  }

  const getStudioInfo = (locationName: string) => {
    for (const [studio, info] of Object.entries(STUDIO_INFO)) {
      if (locationName.toLowerCase().includes(studio.toLowerCase())) {
        return { name: `Barry's ${studio}`, ...info }
      }
    }
    return { name: `Barry's ${locationName}`, address: locationName, phone: '(888) 701-0600' }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    })
  }

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ')
  }

  const formatBillingCycleDates = (cycleId: string, membership: MembershipInfo) => {
    const cycle = membership.billingCycles.find(c => c.id === cycleId)
    if (!cycle) return cycleId
    const start = new Date(cycle.startDatetime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    const end = new Date(cycle.endDatetime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    return `${start} - ${end}`
  }

  if (!marianaTekId) {
    return (
      <Container>
        <Title>FSA/HSA Receipts</Title>
        <NoDataMessage>No Mariana Tek ID found for this user. Cannot generate FSA receipts.</NoDataMessage>
      </Container>
    )
  }

  return (
    <Container>
      <Title>FSA/HSA Receipts</Title>

      {/* Step 1: Select Memberships and Credit Packs */}
      <Section>
        <SectionTitle>Step 1: Select Memberships</SectionTitle>
        {isLoadingMemberships ? (
          <LoadingRow><Spinner />Loading membership instances...</LoadingRow>
        ) : memberships.length === 0 ? (
          <NoDataMessage>No membership instances found for this client.</NoDataMessage>
        ) : (
          <>
            <SelectAllRow>
              <Checkbox
                type="checkbox"
                id="select-all"
                checked={selectedMemberships.size === memberships.length && memberships.length > 0}
                onChange={handleSelectAll}
              />
              <SelectAllLabel htmlFor="select-all">
                {selectedMemberships.size === memberships.length ? 'Deselect All' : 'Select All'}
                {selectedMemberships.size > 0 && ` (${selectedMemberships.size} selected)`}
              </SelectAllLabel>
            </SelectAllRow>
            <MembershipList>
              {memberships.map(membership => (
                <MembershipCard
                  key={membership.id}
                  selected={selectedMemberships.has(membership.id)}
                  onClick={() => handleMembershipToggle(membership)}
                >
                  <Checkbox
                    type="checkbox"
                    checked={selectedMemberships.has(membership.id)}
                    onChange={(e) => { e.stopPropagation(); handleMembershipToggle(membership) }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <MembershipContent>
                    <MembershipName>
                      {membership.name}
                      <StatusBadge status={membership.status}>{formatStatus(membership.status)}</StatusBadge>
                    </MembershipName>
                    <MembershipDetails>
                      ID: {membership.id} |
                      Started: {formatDate(membership.startDate)}
                      {membership.classCount > 0 && ` | ${membership.classCount} classes/cycle`}
                      {membership.renewalRate > 0 && ` | Membership Cost: ${formatCurrency(membership.renewalRate)}`}
                      {membership.billingCycles.length > 0 && ` | ${membership.billingCycles.length} billing cycles`}
                    </MembershipDetails>
                  </MembershipContent>
                </MembershipCard>
              ))}
            </MembershipList>
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>Credit Packs</SectionTitle>
        {isLoadingCreditPacks ? (
          <LoadingRow><Spinner />Loading credit packs...</LoadingRow>
        ) : creditPacks.length === 0 ? (
          <NoDataMessage>No credit packs found for this client.</NoDataMessage>
        ) : (
          <>
            <SelectAllRow>
              <Checkbox
                type="checkbox"
                id="select-all-credits"
                checked={selectedCreditPacks.size === creditPacks.length && creditPacks.length > 0}
                onChange={handleSelectAllCreditPacks}
              />
              <SelectAllLabel htmlFor="select-all-credits">
                {selectedCreditPacks.size === creditPacks.length ? 'Deselect All' : 'Select All'}
                {selectedCreditPacks.size > 0 && ` (${selectedCreditPacks.size} selected)`}
              </SelectAllLabel>
            </SelectAllRow>
            <MembershipList>
              {creditPacks.map(pack => (
                <MembershipCard
                  key={pack.id}
                  selected={selectedCreditPacks.has(pack.id)}
                  onClick={() => handleCreditPackToggle(pack)}
                >
                  <Checkbox
                    type="checkbox"
                    checked={selectedCreditPacks.has(pack.id)}
                    onChange={(e) => { e.stopPropagation(); handleCreditPackToggle(pack) }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <MembershipContent>
                    <MembershipName>
                      {pack.name}
                      <StatusBadge status={pack.isExpired ? 'deactivated' : 'active'}>
                        {pack.isExpired ? 'expired' : `${pack.remainingCredits} remaining`}
                      </StatusBadge>
                    </MembershipName>
                    <MembershipDetails>
                      ID: {pack.id} |
                      Purchased: {formatDate(pack.purchaseDate)} |
                      {pack.totalCredits} credits |
                      {pack.purchaseCost > 0 && `Cost: ${formatCurrency(pack.purchaseCost)} | `}
                      Expires: {pack.expirationDate ? formatDate(pack.expirationDate) : 'N/A'}
                    </MembershipDetails>
                  </MembershipContent>
                </MembershipCard>
              ))}
            </MembershipList>
          </>
        )}
      </Section>

      {(selectedMemberships.size > 0 || selectedCreditPacks.size > 0) && (
        <Section>
          <ButtonGroup>
            <Button variant="primary" onClick={handleLoadClasses} disabled={isLoadingClasses}>
              {isLoadingClasses ? 'Loading Classes...' : `Load Classes for ${selectedMemberships.size + selectedCreditPacks.size} Selection${(selectedMemberships.size + selectedCreditPacks.size) > 1 ? 's' : ''}`}
            </Button>
          </ButtonGroup>
          {isLoadingClasses && loadingProgress && (
            <LoadingRow><Spinner />{loadingProgress}</LoadingRow>
          )}
        </Section>
      )}

      {/* Step 2: Classes by Membership and Credit Pack */}
      {(membershipsWithClasses.length > 0 || creditPacksWithClasses.length > 0) && (
        <Section>
          <SectionTitle>
            Step 2: Classes Attended ({
              membershipsWithClasses.reduce((sum, m) => sum + m.classes.length, 0) +
              creditPacksWithClasses.reduce((sum, p) => sum + p.classes.length, 0)
            } total)
          </SectionTitle>

          {membershipsWithClasses.map((group) => (
            <div key={group.membership.id}>
              <CollapsibleHeader
                expanded={expandedMemberships.has(group.membership.id)}
                onClick={() => toggleExpanded(group.membership.id)}
              >
                <MembershipGroupHeader style={{ flex: 1, marginTop: 0 }}>
                  <ExpandIcon expanded={expandedMemberships.has(group.membership.id)}>&#9654;</ExpandIcon>
                  {' '}{group.membership.name}
                  {' '}<StatusBadge status={group.membership.status}>{formatStatus(group.membership.status)}</StatusBadge>
                  {' '} - Started {formatDate(group.membership.startDate)}
                  {' '} - {group.classes.length} classes found
                  {group.membership.renewalRate > 0 && ` - ${formatCurrency(group.membership.renewalRate)}`}
                </MembershipGroupHeader>
              </CollapsibleHeader>

              {!expandedMemberships.has(group.membership.id) ? null : group.classes.length === 0 ? (
                <NoDataMessage style={{ marginBottom: '1rem' }}>
                  No classes found for this membership.
                </NoDataMessage>
              ) : (
                <>
                  {Array.from(group.classesByBillingCycle.entries()).map(([cycleId, classes]) => (
                    <div key={cycleId}>
                      {group.membership.billingCycles.length > 0 && cycleId !== 'all' && (
                        <BillingCycleHeader>
                          Billing Cycle: {formatBillingCycleDates(cycleId, group.membership)} ({classes.length} classes)
                        </BillingCycleHeader>
                      )}
                      <ClassList>
                        {classes.map(attendance => (
                          <ClassCard key={attendance.id}>
                            <ClassInfo>
                              <ClassDetail><ClassLabel>Date:</ClassLabel>{attendance.date}</ClassDetail>
                              <ClassDetail><ClassLabel>Time:</ClassLabel>{attendance.time}</ClassDetail>
                              <ClassDetail><ClassLabel>Class:</ClassLabel>{attendance.classType}</ClassDetail>
                              <ClassDetail><ClassLabel>Location:</ClassLabel>{attendance.location}</ClassDetail>
                              <ClassDetail><ClassLabel>Duration:</ClassLabel>{attendance.duration} minutes</ClassDetail>
                              <ClassDetail><ClassLabel>Instructor:</ClassLabel>{attendance.instructor}</ClassDetail>
                              {attendance.costPerClass > 0 && (
                                <ClassDetail><ClassLabel>Cost:</ClassLabel>{formatCurrency(attendance.costPerClass)}</ClassDetail>
                              )}
                            </ClassInfo>
                          </ClassCard>
                        ))}
                      </ClassList>
                    </div>
                  ))}
                  {group.membership.renewalRate > 0 && (
                    <ReceiptSubtotal>
                      <span>Membership Cost ({group.classes.length} classes):</span>
                      <span>{formatCurrency(group.membership.renewalRate)}</span>
                    </ReceiptSubtotal>
                  )}
                </>
              )}
            </div>
          ))}

          {creditPacksWithClasses.map((group) => (
            <div key={group.creditPack.id}>
              <CollapsibleHeader
                expanded={expandedCreditPacks.has(group.creditPack.id)}
                onClick={() => toggleCreditPackExpanded(group.creditPack.id)}
              >
                <MembershipGroupHeader style={{ flex: 1, marginTop: 0 }}>
                  <ExpandIcon expanded={expandedCreditPacks.has(group.creditPack.id)}>&#9654;</ExpandIcon>
                  {' '}{group.creditPack.name}
                  {' '}<StatusBadge status={group.creditPack.isExpired ? 'deactivated' : 'active'}>
                    {group.creditPack.isExpired ? 'expired' : `${group.creditPack.remainingCredits} remaining`}
                  </StatusBadge>
                  {' '} - Purchased {formatDate(group.creditPack.purchaseDate)}
                  {' '} - {group.classes.length} classes found
                  {' '} - {group.creditPack.totalCredits} total credits
                  {group.creditPack.purchaseCost > 0 && ` - ${formatCurrency(group.creditPack.purchaseCost)}`}
                </MembershipGroupHeader>
              </CollapsibleHeader>

              {!expandedCreditPacks.has(group.creditPack.id) ? null : group.classes.length === 0 ? (
                <NoDataMessage style={{ marginBottom: '1rem' }}>
                  No classes found for this credit pack.
                </NoDataMessage>
              ) : (
                <>
                  <ClassList>
                    {group.classes.map(attendance => (
                      <ClassCard key={attendance.id}>
                        <ClassInfo>
                          <ClassDetail><ClassLabel>Date:</ClassLabel>{attendance.date}</ClassDetail>
                          <ClassDetail><ClassLabel>Time:</ClassLabel>{attendance.time}</ClassDetail>
                          <ClassDetail><ClassLabel>Class:</ClassLabel>{attendance.classType}</ClassDetail>
                          <ClassDetail><ClassLabel>Location:</ClassLabel>{attendance.location}</ClassDetail>
                          <ClassDetail><ClassLabel>Duration:</ClassLabel>{attendance.duration} minutes</ClassDetail>
                          <ClassDetail><ClassLabel>Instructor:</ClassLabel>{attendance.instructor}</ClassDetail>
                          {attendance.costPerClass > 0 && (
                            <ClassDetail><ClassLabel>Cost:</ClassLabel>{formatCurrency(attendance.costPerClass)}</ClassDetail>
                          )}
                        </ClassInfo>
                      </ClassCard>
                    ))}
                  </ClassList>
                  {group.creditPack.purchaseCost > 0 && (
                    <ReceiptSubtotal>
                      <span>Credit Pack Cost ({group.classes.length} of {group.creditPack.totalCredits} credits used):</span>
                      <span>{formatCurrency(group.creditPack.purchaseCost)}</span>
                    </ReceiptSubtotal>
                  )}
                </>
              )}
            </div>
          ))}

          {(membershipsWithClasses.some(g => g.membership.renewalRate > 0) || creditPacksWithClasses.some(g => g.creditPack.purchaseCost > 0)) && (
            <ReceiptGrandTotal>
              <span>Grand Total:</span>
              <span>{formatCurrency(
                membershipsWithClasses.reduce((sum, g) => sum + g.membership.renewalRate, 0) +
                creditPacksWithClasses.reduce((sum, g) => sum + g.creditPack.purchaseCost, 0)
              )}</span>
            </ReceiptGrandTotal>
          )}

          <ButtonGroup>
            <Button variant="primary" onClick={handleGenerateReceipt}>
              Generate FSA Receipt
            </Button>
          </ButtonGroup>
        </Section>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* Step 3: Receipt Preview */}
      {showReceipt && (membershipsWithClasses.length > 0 || creditPacksWithClasses.length > 0) && (
        <Section>
          <SectionTitle>Step 3: FSA Receipt Preview</SectionTitle>
          <ReceiptPreview id="fsa-receipt">
            <ReceiptHeader>
              <ReceiptLogo
                src="https://barrys-admin-site-2125f87dd7a6.herokuapp.com/barry-logo.jpg"
                alt="Barry's"
                style={{ height: '40px', marginBottom: '0.5rem', objectFit: 'contain' }}
              />
              <ReceiptSubtitle>FSA/HSA Eligible Receipt for Health & Fitness Services</ReceiptSubtitle>
            </ReceiptHeader>

            <ReceiptSection>
              <ReceiptSectionTitle>1. Patient / Covered Person Information</ReceiptSectionTitle>
              <ReceiptText>
                <strong>Name:</strong> {clientName || 'N/A'}
              </ReceiptText>
            </ReceiptSection>

            <ReceiptSection>
              <ReceiptSectionTitle>2. Provider / Merchant Information</ReceiptSectionTitle>
              {(() => {
                const firstClass = membershipsWithClasses.find(m => m.classes.length > 0)?.classes[0]
                  || creditPacksWithClasses.find(p => p.classes.length > 0)?.classes[0]
                const studioInfo = getStudioInfo(firstClass?.location || '')
                return (
                  <ReceiptText>
                    <strong>Provider Name:</strong> {studioInfo.name}<br />
                    <strong>Address:</strong> {studioInfo.address}<br />
                    <strong>Phone:</strong> {studioInfo.phone}
                  </ReceiptText>
                )
              })()}
            </ReceiptSection>

            <ReceiptSection>
              <ReceiptSectionTitle>3. Dates of Service & 4. Detailed Description of Services</ReceiptSectionTitle>
              <ReceiptText>
                <strong>Service Type:</strong> High-Intensity Interval Training (HIIT) Fitness Classes
              </ReceiptText>

              {membershipsWithClasses.map((group, groupIndex) => (
                <div key={group.membership.id} style={{ marginTop: groupIndex > 0 ? '2rem' : '1rem' }}>
                  <ReceiptText style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {group.membership.name} - Started: {formatDate(group.membership.startDate)}
                  </ReceiptText>

                  {group.classes.length > 0 ? (
                    <>
                      <ReceiptTable>
                        <thead>
                          <tr>
                            <ReceiptTableHeader>Date of Service</ReceiptTableHeader>
                            <ReceiptTableHeader>Time</ReceiptTableHeader>
                            <ReceiptTableHeader>Class Type</ReceiptTableHeader>
                            <ReceiptTableHeader>Duration</ReceiptTableHeader>
                            <ReceiptTableHeader>Location</ReceiptTableHeader>
                            <ReceiptTableHeader>Instructor</ReceiptTableHeader>
                            {group.membership.renewalRate > 0 && <ReceiptTableHeader>Amount</ReceiptTableHeader>}
                          </tr>
                        </thead>
                        <tbody>
                          {group.classes.map(attendance => (
                            <tr key={attendance.id}>
                              <ReceiptTableCell>{attendance.date}</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.time}</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.classType}</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.duration} min</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.location}</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.instructor}</ReceiptTableCell>
                              {group.membership.renewalRate > 0 && (
                                <ReceiptTableCell>{formatCurrency(attendance.costPerClass)}</ReceiptTableCell>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </ReceiptTable>
                      <ReceiptSubtotal>
                        <span>Membership Cost ({group.classes.length} classes):</span>
                        <span>{group.membership.renewalRate > 0 ? formatCurrency(group.membership.renewalRate) : `${group.classes.length} classes attended`}</span>
                      </ReceiptSubtotal>
                    </>
                  ) : (
                    <ReceiptText style={{ fontStyle: 'italic', color: '#666' }}>
                      No classes attended for this membership yet.
                    </ReceiptText>
                  )}
                </div>
              ))}

              {creditPacksWithClasses.map((group, groupIndex) => (
                <div key={group.creditPack.id} style={{ marginTop: (membershipsWithClasses.length > 0 || groupIndex > 0) ? '2rem' : '1rem' }}>
                  <ReceiptText style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {group.creditPack.name} - Purchased: {formatDate(group.creditPack.purchaseDate)}
                    {' '}({group.creditPack.totalCredits} credits{group.creditPack.purchaseCost > 0 && `, ${formatCurrency(group.creditPack.purchaseCost)}`})
                  </ReceiptText>

                  {group.classes.length > 0 ? (
                    <>
                      <ReceiptTable>
                        <thead>
                          <tr>
                            <ReceiptTableHeader>Date of Service</ReceiptTableHeader>
                            <ReceiptTableHeader>Time</ReceiptTableHeader>
                            <ReceiptTableHeader>Class Type</ReceiptTableHeader>
                            <ReceiptTableHeader>Duration</ReceiptTableHeader>
                            <ReceiptTableHeader>Location</ReceiptTableHeader>
                            <ReceiptTableHeader>Instructor</ReceiptTableHeader>
                            {group.creditPack.purchaseCost > 0 && <ReceiptTableHeader>Amount</ReceiptTableHeader>}
                          </tr>
                        </thead>
                        <tbody>
                          {group.classes.map(attendance => (
                            <tr key={attendance.id}>
                              <ReceiptTableCell>{attendance.date}</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.time}</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.classType}</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.duration} min</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.location}</ReceiptTableCell>
                              <ReceiptTableCell>{attendance.instructor}</ReceiptTableCell>
                              {group.creditPack.purchaseCost > 0 && (
                                <ReceiptTableCell>{formatCurrency(attendance.costPerClass)}</ReceiptTableCell>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </ReceiptTable>
                      <ReceiptSubtotal>
                        <span>Credit Pack Cost ({group.classes.length} of {group.creditPack.totalCredits} credits used):</span>
                        <span>{group.creditPack.purchaseCost > 0 ? formatCurrency(group.creditPack.purchaseCost) : `${group.classes.length} classes attended`}</span>
                      </ReceiptSubtotal>
                    </>
                  ) : (
                    <ReceiptText style={{ fontStyle: 'italic', color: '#666' }}>
                      No classes attended with this credit pack yet.
                    </ReceiptText>
                  )}
                </div>
              ))}
            </ReceiptSection>

            <ReceiptSection>
              <ReceiptSectionTitle>5. Cost Breakdown</ReceiptSectionTitle>
              <ReceiptText>
                {membershipsWithClasses.length > 0 && (
                  <><strong>Total Memberships:</strong> {membershipsWithClasses.length}<br /></>
                )}
                {creditPacksWithClasses.length > 0 && (
                  <><strong>Total Credit Packs:</strong> {creditPacksWithClasses.length}<br /></>
                )}
                <strong>Total Classes Attended:</strong> {
                  membershipsWithClasses.reduce((sum, m) => sum + m.classes.length, 0) +
                  creditPacksWithClasses.reduce((sum, p) => sum + p.classes.length, 0)
                }
              </ReceiptText>
              {(membershipsWithClasses.some(g => g.membership.renewalRate > 0) || creditPacksWithClasses.some(g => g.creditPack.purchaseCost > 0)) && (
                <ReceiptGrandTotal>
                  <span>Grand Total:</span>
                  <span>{formatCurrency(
                    membershipsWithClasses.reduce((sum, g) => sum + g.membership.renewalRate, 0) +
                    creditPacksWithClasses.reduce((sum, g) => sum + g.creditPack.purchaseCost, 0)
                  )}</span>
                </ReceiptGrandTotal>
              )}
            </ReceiptSection>

            <ReceiptSection>
              <ReceiptText style={{ fontSize: '1.1rem', color: theme.color.text.grey, marginTop: '2rem' }}>
                This receipt is provided for FSA/HSA reimbursement purposes. Barry's fitness classes
                qualify as eligible medical expenses when prescribed by a healthcare provider for the
                treatment or prevention of a specific medical condition. Please consult with your
                FSA/HSA administrator for specific eligibility requirements.
              </ReceiptText>
            </ReceiptSection>
          </ReceiptPreview>

          {emailSuccess && <SuccessMessage>{emailSuccess}</SuccessMessage>}

          <ButtonGroup>
            <Button variant="primary" onClick={handlePrintReceipt}>
              Print Receipt
            </Button>
            <Button
              variant="secondary"
              onClick={handleEmailReceipt}
              disabled={isSendingEmail || !clientEmail}
            >
              {isSendingEmail ? 'Sending...' : `Email Receipt${clientEmail ? ` to ${clientEmail}` : ''}`}
            </Button>
          </ButtonGroup>
        </Section>
      )}

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #fsa-receipt, #fsa-receipt * {
            visibility: visible;
          }
          #fsa-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
          }
        }
      `}</style>
    </Container>
  )
}

export default FSAReceipts
